/**
 * Heater Safety Clearance — skyddsavstånd till brännbart material
 *
 * Computes the minimum-clearance envelope around the heater (side/front/
 * ceiling distances per BBR 5:4 and manufacturer install manuals) and
 * checks the room layout for intrusions: benches, unshielded walls,
 * partition and ceiling.
 *
 * The default side/front distances assume the protective shields that the
 * engines always render next to wood heaters (strålskydd) — unshielded
 * wood heaters need ~500 mm. All distances are admin-overridable via the
 * Rule Registry.
 *
 * Pure geometry: boxes in, i18n keys + numbers out. The caller (engines
 * layer) derives the boxes from BuildInput — see
 * src/engines/heaterClearanceLayout.ts.
 */
import { getRuleValue, type RuleOverrides } from "../../data/ruleRegistry";
import type { HeaterType } from "../../engines/types";
import { type HeaterSelection, resolveHeaterModel } from "./heaterCatalog";

// ─── Types ──────────────────────────────────────────────────

/** Axis-aligned box in meters, in the engine's room-local frame. */
export interface ClearanceBox {
	minX: number;
	maxX: number;
	minY: number;
	maxY: number;
	minZ: number;
	maxZ: number;
}

export type HeaterClearanceObstacleKind =
	| "wall"
	| "bench"
	| "ceiling"
	| "partition";

export type HeaterClearanceObstacleLabelKey =
	| "domain_heaterClearance_obstacle_wall"
	| "domain_heaterClearance_obstacle_bench"
	| "domain_heaterClearance_obstacle_ceiling"
	| "domain_heaterClearance_obstacle_partition";

export interface HeaterClearanceObstacle {
	kind: Exclude<HeaterClearanceObstacleKind, "wall" | "ceiling">;
	labelKey: HeaterClearanceObstacleLabelKey;
	box: ClearanceBox;
}

/**
 * Horizontal directions (room frame) where the layout provides a
 * protective shield, so the wall behind it is exempt from the
 * combustible-clearance check.
 */
export interface ShieldedDirections {
	posX?: boolean;
	negX?: boolean;
	posZ?: boolean;
	negZ?: boolean;
}

export interface HeaterClearanceViolation {
	obstacleKind: HeaterClearanceObstacleKind;
	obstacleLabelKey: HeaterClearanceObstacleLabelKey;
	/** Resolve at the render boundary with {requiredMm}/{actualMm} args. */
	messageKey: "domain_heaterClearance_violation";
	requiredMm: number;
	actualMm: number;
}

export interface HeaterClearanceValues {
	sideMm: number;
	frontMm: number;
	ceilingMm: number;
}

export interface HeaterClearanceResult {
	/** Required-clearance envelope, clamped to the room, for 3D display. */
	envelope: ClearanceBox;
	heaterBox: ClearanceBox;
	clearances: HeaterClearanceValues;
	violations: HeaterClearanceViolation[];
	status: "ok" | "violation";
}

export interface HeaterClearanceInput {
	heaterType: HeaterType;
	/** A catalog model's datasheet distances replace the rules (issue 159). */
	heaterModelId?: string | null;
	heaterBox: ClearanceBox;
	/**
	 * Inner volume of the sauna room. maxY must be the ceiling underside
	 * at the heater position (sloped ceilings vary across the room).
	 */
	roomInnerBox: ClearanceBox;
	shieldedDirections: ShieldedDirections;
	obstacles: HeaterClearanceObstacle[];
}

// ─── Internals ──────────────────────────────────────────────

/** Engine geometry places shields at exactly the rule distance — a value
 *  that rounds to the required mm must pass, so compare with 0.5 mm slack. */
const TOLERANCE_MM = 0.5;

const WALL_LABEL_KEY: HeaterClearanceObstacleLabelKey =
	"domain_heaterClearance_obstacle_wall";
const CEILING_LABEL_KEY: HeaterClearanceObstacleLabelKey =
	"domain_heaterClearance_obstacle_ceiling";

function toMm(meters: number): number {
	return Math.round(meters * 1000);
}

/** Face-to-face gap between two boxes along one axis (negative = overlap). */
function axisGap(
	aMin: number,
	aMax: number,
	bMin: number,
	bMax: number,
): number {
	if (bMin >= aMax) return bMin - aMax;
	if (bMax <= aMin) return aMin - bMax;
	return -Math.min(aMax - bMin, bMax - aMin);
}

function boxesOverlapVertically(
	heater: ClearanceBox,
	obstacle: ClearanceBox,
): boolean {
	return obstacle.minY < heater.maxY && obstacle.maxY > heater.minY;
}

// ─── Public API ─────────────────────────────────────────────

/**
 * The distances a design's heater must keep. A chosen catalog model brings
 * its datasheet's; any distance the datasheet does not list, and every
 * distance without a model, comes from the type's `heater.clearance.*` rule.
 *
 * The one source both this check and `validateInteriorLayout` read, so the
 * plan view and the 3D can never disagree about the same unit.
 */
export function resolveHeaterClearanceMm(
	selection: HeaterSelection,
	overrides: RuleOverrides = {},
): HeaterClearanceValues {
	const prefix =
		selection.heaterType === "wood"
			? "heater.clearance.wood"
			: "heater.clearance.electric";
	const model = resolveHeaterModel(selection)?.clearanceMm;
	return {
		sideMm: model?.side ?? getRuleValue<number>(`${prefix}.sideMm`, overrides),
		frontMm:
			model?.front ?? getRuleValue<number>(`${prefix}.frontMm`, overrides),
		ceilingMm:
			model?.ceiling ?? getRuleValue<number>(`${prefix}.ceilingMm`, overrides),
	};
}

export function calculateHeaterClearance(
	input: HeaterClearanceInput,
	overrides: RuleOverrides = {},
): HeaterClearanceResult {
	const { sideMm, frontMm, ceilingMm } = resolveHeaterClearanceMm(
		input,
		overrides,
	);

	const sideM = sideMm / 1000;
	const frontM = frontMm / 1000;
	const ceilingM = ceilingMm / 1000;
	const { heaterBox, roomInnerBox, shieldedDirections, obstacles } = input;

	const violations: HeaterClearanceViolation[] = [];

	const addViolation = (
		kind: HeaterClearanceObstacleKind,
		labelKey: HeaterClearanceObstacleLabelKey,
		requiredMm: number,
		actualM: number,
	) => {
		const actualMm = Math.max(0, toMm(actualM));
		if (actualMm < requiredMm - TOLERANCE_MM) {
			violations.push({
				obstacleKind: kind,
				obstacleLabelKey: labelKey,
				messageKey: "domain_heaterClearance_violation",
				requiredMm,
				actualMm,
			});
		}
	};

	// 1. Unshielded room walls (side = X axis, front = Z axis).
	if (!shieldedDirections.negX) {
		addViolation(
			"wall",
			WALL_LABEL_KEY,
			sideMm,
			heaterBox.minX - roomInnerBox.minX,
		);
	}
	if (!shieldedDirections.posX) {
		addViolation(
			"wall",
			WALL_LABEL_KEY,
			sideMm,
			roomInnerBox.maxX - heaterBox.maxX,
		);
	}
	if (!shieldedDirections.negZ) {
		addViolation(
			"wall",
			WALL_LABEL_KEY,
			frontMm,
			heaterBox.minZ - roomInnerBox.minZ,
		);
	}
	if (!shieldedDirections.posZ) {
		addViolation(
			"wall",
			WALL_LABEL_KEY,
			frontMm,
			roomInnerBox.maxZ - heaterBox.maxZ,
		);
	}

	// 2. Ceiling: heater top stones → ceiling underside at heater position.
	addViolation(
		"ceiling",
		CEILING_LABEL_KEY,
		ceilingMm,
		roomInnerBox.maxY - heaterBox.maxY,
	);

	// 3. Combustible obstacles (benches, partition). Only obstacles that
	//    overlap the heater's height band matter; the governing distance
	//    is the larger axis gap (a bench diagonally away is fine if it
	//    clears on either axis).
	for (const obstacle of obstacles) {
		if (!boxesOverlapVertically(heaterBox, obstacle.box)) continue;

		const gapX = axisGap(
			heaterBox.minX,
			heaterBox.maxX,
			obstacle.box.minX,
			obstacle.box.maxX,
		);
		const gapZ = axisGap(
			heaterBox.minZ,
			heaterBox.maxZ,
			obstacle.box.minZ,
			obstacle.box.maxZ,
		);
		const okX = toMm(gapX) >= sideMm - TOLERANCE_MM;
		const okZ = toMm(gapZ) >= frontMm - TOLERANCE_MM;
		if (okX || okZ) continue;

		// Report against the axis that is closest to passing.
		const reportOnX = sideMm - toMm(gapX) <= frontMm - toMm(gapZ);
		addViolation(
			obstacle.kind,
			obstacle.labelKey,
			reportOnX ? sideMm : frontMm,
			reportOnX ? gapX : gapZ,
		);
	}

	// 4. Display envelope: heater inflated by the required distances,
	//    clamped to the room so it never pokes through walls.
	const envelope: ClearanceBox = {
		minX: Math.max(roomInnerBox.minX, heaterBox.minX - sideM),
		maxX: Math.min(roomInnerBox.maxX, heaterBox.maxX + sideM),
		minY: Math.max(roomInnerBox.minY, heaterBox.minY),
		maxY: Math.min(roomInnerBox.maxY, heaterBox.maxY + ceilingM),
		minZ: Math.max(roomInnerBox.minZ, heaterBox.minZ - frontM),
		maxZ: Math.min(roomInnerBox.maxZ, heaterBox.maxZ + frontM),
	};

	return {
		envelope,
		heaterBox,
		clearances: { sideMm, frontMm, ceilingMm },
		violations,
		status: violations.length > 0 ? "violation" : "ok",
	};
}
